import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import SEO from "../components/SEO/SEO";
import ShareButtons from "../components/ui/share-buttons";
import { Header, Footer } from "../components/SiteUI/index";
import posts from "../shared/api/posts";

const ArticlesPage = () => {
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalPosts: 0 });

    useEffect(() => {
        fetchArticles();
    }, []);

    const fetchArticles = async (page = 1) => {
        setLoading(true);
        try {
            const res = await posts.getPublishedPosts(page, 12);
            setArticles(res.posts);
            setPagination({
                currentPage: res.currentPage,
                totalPages: res.totalPages,
                totalPosts: res.totalPosts
            });
        } catch (err) {
            console.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (date) => {
        const options = { year: 'numeric', month: 'short', day: 'numeric' };
        return new Date(date).toLocaleDateString('ru-RU', options);
    };

    function stripHTMLTags(str) {
        return str.replace(/<[^>]*>/g, '').slice(0, 150).concat('...');
    }

    const handlePageChange = (page) => {
        fetchArticles(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    if (loading) {
        return (
            <>
                <SEO 
                    title="Загрузка статей"
                    description="Загружаем все статьи по психологии и саморазвитию"
                />
                <div className="min-h-screen bg-olive-50 flex items-center justify-center">
                    <div className="text-olive-900">Загрузка статей...</div>
                </div>
            </>
        );
    }

    return (
        <>
            <SEO 
                title="Все статьи по психологии"
                description={`Полный архив статей по психологии и саморазвитию. ${pagination.totalPosts} статей от профессионального психолога в Минске. Практические советы и рекомендации.`}
                keywords="психология статьи, психологические статьи, саморазвитие, психотерапия, консультация психолога, психолог минск, помощь психолога"
                url="/articles"
                structuredData={{
                    "@context": "https://schema.org",
                    "@type": "CollectionPage",
                    "name": "Все статьи по психологии",
                    "description": `Полный архив статей по психологии и саморазвитию. ${pagination.totalPosts} статей от профессионального психолога в Минске.`,
                    "url": "https://cope.by/articles",
                    "mainEntity": {
                        "@type": "ItemList",
                        "numberOfItems": pagination.totalPosts,
                        "itemListElement": articles.slice(0, 10).map((article, index) => ({
                            "@type": "Article",
                            "position": index + 1,
                            "name": article.title,
                            "url": "https://cope.by/article/${article.slug}",
                            "datePublished": article.createdAt
                        }))
                    }
                }}
            />
            <div className="min-h-screen bg-white text-olive-900">
            <Header />
            <main className="pt-20">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div className="mb-8">
                    <div className="flex items-center justify-between mb-4">
                        <h1 className="font-serif text-4xl text-olive-950">Все статьи</h1>
                        <ShareButtons 
                            title="Все статьи по психологии"
                            description={`Полный архив статей по психологии и саморазвитию. ${pagination.totalPosts} статей от профессионального психолога в Минске.`}
                            url="/articles"
                            showText={false}
                        />
                    </div>
                    <p className="text-olive-900/70 text-lg">
                        Всего статей: {pagination.totalPosts}
                    </p>
                </div>

                {articles.length === 0 ? (
                    <div className="text-center py-12">
                        <p className="text-olive-900/70 text-lg">Статей пока нет.</p>
                    </div>
                ) : (
                    <>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                            {articles.map((p) => (
                                <Card key={p._id} className="flex flex-col bg-white/5 backdrop-blur-sm border-olive-100 hover:shadow-md transition-shadow">
                                    <CardHeader>
                                        <CardTitle className="font-serif text-olive-950">{p.title}</CardTitle>
                                    </CardHeader>
                                    <CardContent className="flex flex-1 flex-col gap-4">
                                        <p className="flex-1 text-olive-900/70 text-sm leading-relaxed mb-4">
                                            {stripHTMLTags(p.content)}
                                        </p>
                                        <div className="mt-4 flex items-center justify-between text-sm text-olive-900/60">
                                            <span>{formatDate(p.createdAt)}</span>
                                            <Button
                                                asChild
                                                variant="ghost"
                                                className="text-olive-800 hover:bg-olive-200 hover:text-white"
                                            >
                                                <Link to={`/article/${p.slug}`}>
                                                    Читать <ArrowRight className="w-4 h-4 ml-1" />
                                                </Link>
                                            </Button>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>

                        {pagination.totalPages > 1 && (
                            <div className="flex justify-center items-center space-x-2">
                                <Button
                                    variant="outline"
                                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                                    disabled={pagination.currentPage === 1}
                                    className="text-olive-800 border-olive-200 hover:bg-olive-100"
                                >
                                    Назад
                                </Button>
                                
                                <div className="flex space-x-1">
                                    {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((page) => (
                                        <Button
                                            key={page}
                                            variant={page === pagination.currentPage ? "default" : "outline"}
                                            onClick={() => handlePageChange(page)}
                                            className={`${
                                                page === pagination.currentPage
                                                    ? "bg-olive-800 text-white"
                                                    : "text-olive-800 border-olive-200 hover:bg-olive-100"
                                            }`}
                                        >
                                            {page}
                                        </Button>
                                    ))}
                                </div>
                                
                                <Button
                                    variant="outline"
                                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                                    disabled={pagination.currentPage === pagination.totalPages}
                                    className="text-olive-800 border-olive-200 hover:bg-olive-100"
                                >
                                    Вперед
                                </Button>
                            </div>
                        )}
                    </>
                )}
            </div>
            </main>
            <Footer />
            </div>
        </>
    );
};

export default ArticlesPage;
